package com.schoolconnect.app.feature.students

import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.ServerPagingState

data class StudentGuardianUi(
    val name: String,
    val relationCode: String,
    val mobile: String,
    val email: String
)

data class StudentRowUi(
    val id: String,
    val admissionNo: String,
    val studentName: String,
    val className: String,
    val section: String,
    val rollNo: String,
    val genderCode: String,
    val bloodGroupCode: String,
    val subjectCodes: List<String>,
    val statusCode: String,
    val createdDate: String,
    val updatedDate: String,
    val createdAtText: String,
    val updatedAtText: String,
    val createdBy: String,
    val updatedBy: String,
    val primaryGuardian: StudentGuardianUi,
    val secondaryGuardian: StudentGuardianUi,
    val dateOfBirth: String = ""
)

data class StudentFormValues(
    val id: String = "",
    val admissionNo: String = "",
    val studentName: String = "",
    val className: String = "",
    val section: String = "",
    val classSectionId: String = "",
    val rollNo: String = "",
    val genderCode: String = "",
    val dob: String = "",
    val bloodGroupCode: String = "",
    val subjectCodes: List<String> = defaultStudentSubjects(),
    val statusCode: String = SchoolKeys.V_STATUS_ACTIVE
)

data class StudentFormErrors(
    val admissionNo: String = "",
    val studentName: String = "",
    val className: String = "",
    val section: String = "",
    val rollNo: String = "",
    val genderCode: String = "",
    val dob: String = "",
    val bloodGroupCode: String = "",
    val subjectCodes: String = "",
    val statusCode: String = ""
)

data class StudentClassSectionOption(
    val id: String,
    val className: String,
    val sectionName: String,
    val subjectCodes: List<String> = emptyList()
)

data class StudentClassSectionPickerUi(
    val options: List<StudentClassSectionOption> = emptyList(),
    val query: String = "",
    val pageIndex: Int = 0,
    val totalCount: Int = 0,
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val hasMore: Boolean = true,
    val errorMessage: String = ""
)

data class StudentFilterValues(
    val className: String = "",
    val section: String = "",
    val genderCode: String = "",
    val statusCode: String = "",
    val bloodGroupCode: String = "",
    val subjectCodes: List<String> = emptyList(),
    val createdFrom: String = "",
    val createdTo: String = ""
)

/** Sort keys accepted by POST /api/v1/students/filter-with-class-sections. */
enum class StudentSortField(val apiField: String) {
    ADMISSION_NO("admission_number"),
    STUDENT_NAME("name"),
    CLASS("class_name"),
    SECTION("section_name"),
    ROLL_NO("roll_number"),
    GENDER("gender"),
    STATUS("is_active")
}

data class StudentsPageUi(
    val items: List<StudentRowUi>,
    val totalCount: Int,
    val startIndex: Int,
    val endIndex: Int,
    val pageCount: Int
)

data class StudentsUiState(
    val schoolId: String = "",
    val paging: ServerPagingState = ServerPagingState(),
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    val errorMessage: String = "",
    val apiErrorMessage: String = "",
    val infoMessage: String = "",
    val searchQuery: String = "",
    val filters: StudentFilterValues = StudentFilterValues(),
    val sortField: StudentSortField = StudentSortField.ADMISSION_NO,
    val sortAscending: Boolean = true,
    val students: List<StudentRowUi> = emptyList(),
    val classSectionLabels: Map<String, String> = emptyMap(),
    val subjectOptions: List<StudentSubjectOption> = emptyList(),
    val classSectionOptions: List<StudentClassSectionOption> = emptyList(),
    val classSectionPicker: StudentClassSectionPickerUi = StudentClassSectionPickerUi(),
    val studentFilterClassOptions: List<String> = emptyList(),
    val studentFilterSectionOptions: List<String> = emptyList(),
    val studentFilterSectionsByClass: Map<String, List<String>> = emptyMap(),
    val editForm: StudentFormValues? = null,
    val mutationSuccessNonce: Long = 0L
)

sealed interface StudentsUiEvent {
    data object BackClicked : StudentsUiEvent
    data object RefreshRequested : StudentsUiEvent
    data class SearchChanged(val query: String) : StudentsUiEvent
    data class FiltersApplied(val searchQuery: String, val values: StudentFilterValues) : StudentsUiEvent
    data object FiltersReset : StudentsUiEvent
    data class SortChanged(val field: StudentSortField) : StudentsUiEvent
    data class PageChanged(val pageIndex: Int) : StudentsUiEvent
    data class PageSizeChanged(val pageSize: Int) : StudentsUiEvent
    data class ClassSectionPickerSearchChanged(val query: String) : StudentsUiEvent
    data class ClassSectionPickerPageChanged(val pageIndex: Int) : StudentsUiEvent
    data object ClassSectionPickerLoadMore : StudentsUiEvent
    data object ClassSectionPickerOpened : StudentsUiEvent
    data class SaveStudent(val values: StudentFormValues) : StudentsUiEvent
    data object StudentFormDismissed : StudentsUiEvent
    data class EditStudent(val student: StudentRowUi) : StudentsUiEvent
    data class ToggleStatus(val studentId: String) : StudentsUiEvent
    data class DeleteStudent(val studentId: String) : StudentsUiEvent
    data object ClearMessages : StudentsUiEvent
}

val StudentsUiState.pageIndex: Int get() = paging.pageIndex
val StudentsUiState.pageSize: Int get() = paging.pageSize

internal val studentSeedRows: List<StudentRowUi> = buildStudentSeedRows()

internal fun defaultStudentSubjects(): List<String> = emptyList()

internal fun buildStudentSeedRows(): List<StudentRowUi> {
    val subjectCombos = listOf(
        listOf("MAT", "SCI", "ENG", "SST", "HIN"),
        listOf("MAT", "SCI", "ENG"),
        listOf("ENG", "HIN", "SST"),
        listOf("MAT", "SCI", "COMP"),
        listOf("ENG", "GK", "HIN")
    )
    val monthNames = listOf(
        "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    )
    val bloodGroups = listOf(
        SchoolKeys.V_BLOOD_GROUP_A_POS,
        SchoolKeys.V_BLOOD_GROUP_B_POS,
        SchoolKeys.V_BLOOD_GROUP_AB_POS,
        SchoolKeys.V_BLOOD_GROUP_O_POS,
        SchoolKeys.V_BLOOD_GROUP_A_NEG,
        SchoolKeys.V_BLOOD_GROUP_B_NEG,
        SchoolKeys.V_BLOOD_GROUP_AB_NEG,
        SchoolKeys.V_BLOOD_GROUP_O_NEG
    )
    val firstNames = listOf(
        "Aarav", "Diya", "Rohan", "Meera", "Rahul", "Sneha", "Kiran", "Anaya",
        "Aditya", "Ishita", "Kabir", "Pooja", "Nikhil", "Riya", "Arjun", "Maya"
    )
    val lastNames = listOf(
        "Kumar", "Joseph", "Pai", "Nair", "Rao", "Sharma", "Patel", "Shetty"
    )
    val inactiveIndices = buildSet {
        add(5)
        for (n in 2..27) add(n * 46)
    }

    val rows = mutableListOf<StudentRowUi>()
    for (index in 1..1248) {
        val admissionNo = "ADM" + index.toString().padStart(3, '0')
        val name = if (index == 1) {
            "Aarav Kumar"
        } else {
            "${firstNames[(index - 1) % firstNames.size]} ${lastNames[(index - 1) % lastNames.size]}"
        }
        val className = when (index) {
            1 -> "5"
            2 -> "4"
            3 -> "6"
            4 -> "7"
            5 -> "8"
            6 -> "9"
            7 -> "10"
            else -> (((index - 1) % 10) + 1).toString()
        }
        val section = when (index) {
            1 -> "A"
            2 -> "B"
            3 -> "A"
            4 -> "C"
            5 -> "B"
            6 -> "A"
            7 -> "A"
            else -> listOf("A", "B", "C", "D")[(index - 1) % 4]
        }
        val rollNo = when (index) {
            1 -> "12"
            2 -> "08"
            3 -> "15"
            4 -> "03"
            5 -> "21"
            6 -> "07"
            7 -> "11"
            else -> ((index - 1) % 35 + 1).toString().padStart(2, '0')
        }
        val genderCode = when (index) {
            2, 4, 6 -> SchoolKeys.V_GENDER_F
            else -> SchoolKeys.V_GENDER_M
        }
        val bloodGroupCode = when (index) {
            1 -> SchoolKeys.V_BLOOD_GROUP_B_POS
            2 -> SchoolKeys.V_BLOOD_GROUP_A_POS
            3 -> SchoolKeys.V_BLOOD_GROUP_O_POS
            4 -> SchoolKeys.V_BLOOD_GROUP_AB_POS
            5 -> SchoolKeys.V_BLOOD_GROUP_B_NEG
            6 -> SchoolKeys.V_BLOOD_GROUP_A_NEG
            7 -> SchoolKeys.V_BLOOD_GROUP_B_POS
            else -> bloodGroups[(index - 1) % bloodGroups.size]
        }
        val subjectCodes = subjectCombos[(index - 1) % subjectCombos.size]
        val isActive = !inactiveIndices.contains(index)
        val day = ((index - 1) % 28) + 1
        val month = monthNames[(index - 1) % monthNames.size]
        val createdDate = "${day.toString().padStart(2, '0')} $month 2024"
        val updatedDate = "${((day + 12 - 1) % 28 + 1).toString().padStart(2, '0')} ${monthNames[(index + 2) % monthNames.size]} 2024"
        val createdAtText = if (index == 1) "15 May 2024, 10:30 AM" else "$createdDate, 09:${(index % 50).toString().padStart(2, '0')} AM"
        val updatedAtText = if (index == 1) "28 May 2024, 04:45 PM" else "$updatedDate, 03:${(index % 50).toString().padStart(2, '0')} PM"
        val primaryGuardian = if (index == 1) {
            StudentGuardianUi(
                name = "Student Parent",
                relationCode = SchoolKeys.V_REL_FATHER,
                mobile = "9876543210",
                email = "parent@example.com"
            )
        } else {
            StudentGuardianUi(
                name = "${name.substringBefore(' ')} Parent",
                relationCode = SchoolKeys.V_REL_FATHER,
                mobile = "98765${(43210 + index).toString().takeLast(5).padStart(5, '0')}",
                email = "${name.substringBefore(' ').lowercase()}.parent${index}@example.com"
            )
        }
        val secondaryGuardian = if (index == 1) {
            StudentGuardianUi(
                name = "Meera Kumar",
                relationCode = SchoolKeys.V_REL_MOTHER,
                mobile = "9876543211",
                email = "meera.kumar@example.com"
            )
        } else {
            StudentGuardianUi(
                name = "${name.substringBefore(' ')} Guardian",
                relationCode = SchoolKeys.V_REL_MOTHER,
                mobile = "98766${(43210 + index).toString().takeLast(5).padStart(5, '0')}",
                email = "${name.substringBefore(' ').lowercase()}.guardian${index}@example.com"
            )
        }

        rows += StudentRowUi(
            id = index.toString(),
            admissionNo = admissionNo,
            studentName = name,
            className = className,
            section = section,
            rollNo = rollNo,
            genderCode = genderCode,
            bloodGroupCode = bloodGroupCode,
            subjectCodes = subjectCodes,
            statusCode = if (isActive) SchoolKeys.V_STATUS_ACTIVE else SchoolKeys.V_STATUS_INACTIVE,
            createdDate = createdDate,
            updatedDate = updatedDate,
            createdAtText = createdAtText,
            updatedAtText = updatedAtText,
            createdBy = "School Administrator",
            updatedBy = "School Administrator",
            primaryGuardian = primaryGuardian,
            secondaryGuardian = secondaryGuardian
        )
    }
    return rows
}
